import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useInstatDesign } from '../theme/instatDesign';

/**
 * iOS-style segmented control.
 * options: [{ value, label, count?, icon? }]
 * value: current selected value
 * onChange: (value) => void
 * size: 'sm' | 'md' | 'lg'
 */
export default function SegmentedControl({
  options = [],
  value,
  onChange,
  size = 'md',
  fullWidth = false,
  sx = {},
}) {
  const d = useInstatDesign();
  const containerRef = useRef(null);
  const btnRefs = useRef({});
  const [indicator, setIndicator] = useState({ left: 0, width: 0, visible: false });

  const heights = { sm: 32, md: 38, lg: 44 };
  const paddings = { sm: '0 10px', md: '0 14px', lg: '0 18px' };
  const fontSizes = { sm: 12.5, md: 13.5, lg: 14.5 };
  const h = heights[size];

  useLayoutEffect(() => {
    const btn = btnRefs.current[value];
    const container = containerRef.current;
    if (!btn || !container) return;
    const cRect = container.getBoundingClientRect();
    const bRect = btn.getBoundingClientRect();
    setIndicator({
      left: bRect.left - cRect.left - 3,
      width: bRect.width,
      visible: true,
    });
  }, [value, options]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        bgcolor: 'rgba(120,120,128,0.10)',
        borderRadius: `${d.radius.md}px`,
        p: '3px',
        height: h,
        width: fullWidth ? '100%' : 'auto',
        ...sx,
      }}
    >
      {/* Sliding white indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: 3,
          left: indicator.left,
          width: indicator.width,
          height: `calc(100% - 6px)`,
          bgcolor: d.colors.surface,
          borderRadius: `${d.radius.sm}px`,
          boxShadow:
            '0 3px 8px rgba(0,0,0,0.10), 0 3px 1px rgba(0,0,0,0.04)',
          transition: indicator.visible
            ? `left ${d.motion.spring}, width ${d.motion.spring}`
            : 'none',
          pointerEvents: 'none',
        }}
      />
      {options.map((o) => {
        const active = o.value === value;
        return (
          <Box
            key={String(o.value)}
            ref={(el) => (btnRefs.current[o.value] = el)}
            onClick={() => onChange && onChange(o.value)}
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              padding: paddings[size],
              height: h - 6,
              cursor: 'pointer',
              userSelect: 'none',
              color: active ? d.colors.text : d.colors.textSecondary,
              fontWeight: active ? 600 : 500,
              fontSize: fontSizes[size],
              whiteSpace: 'nowrap',
              flex: fullWidth ? 1 : 'unset',
              justifyContent: 'center',
              transition: `color ${d.motion.fast}`,
            }}
          >
            {o.icon}
            {o.label}
            {o.count != null && (
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  color: active ? d.colors.textSecondary : d.colors.textTertiary,
                  fontWeight: 500,
                  ...d.typo.tabular,
                }}
              >
                {o.count}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
